import cx from "classnames";
import React from "react";
import "../styles/post.css";
import formatDate from "../util/formatDate";
import getAuthorImgSrc from "../util/getAuthorImgSrc";
import * as css from "./Metadata.module.css";

type Props = {
  author?: Queries.Maybe<string>;
  authorHref: Queries.Maybe<string>;
  date: Queries.Maybe<string>;
};

export default function Metadata(props: Props) {
  if (!props.author && !props.date) return null;

  if (!props.author && props.date) {
    return (
      <div className={css.authorInfo}>
        <time>{formatDate(props.date)}</time>
      </div>
    );
  }

  if (props.author && props.date) {
    const names = props.author.split(" and ");

    if (names.length === 1) {
      return <OneAuthor {...props} author={props.author} date={props.date} />;
    }

    return (
      <ManyAuthors
        authors={names}
        authorHrefs={props.authorHref}
        date={props.date}
      />
    );
  }
  return null;
}

type AuthorProps = {
  author: string;
  authorHref: Queries.Maybe<string>;
  date: string;
};

function OneAuthor({ author, authorHref, date }: AuthorProps) {
  const authorDetails = authorHref ? <a href={authorHref}>{author}</a> : author;

  return (
    <div className={css.metadata}>
      <AuthorImage author={author} className={css.authorImg} />
      <div className={css.metadataText}>
        <span className={css.authorInfo}>
          <div>{authorDetails}</div>
          <time>{formatDate(date)}</time>
        </span>
      </div>
    </div>
  );
}

type ManyAuthorProps = {
  authors: string[];
  authorHrefs: Queries.Maybe<string>;
  date: string;
};

function ManyAuthors({ authors, authorHrefs, date }: ManyAuthorProps) {
  const authorsInfo = authors.map((author, i) => {
    const href = authorHrefs ? authorHrefs.split(", ")[i] : undefined;
    const authorDetails = href ? <a href={href}>{author}</a> : author;
    return (
      <span key={author}>
        {authorDetails}
        {i !== authors.length - 1 && <span className={css.and}>and</span>}
      </span>
    );
  });

  return (
    <div className={cx(css.metadata, css.many)}>
      <div className={css.imgContainer}>
        <AuthorImage author={authors[0]} className={css.circleLeft} />
        <AuthorImage author={authors[1]} className={css.circleRight} />
      </div>
      <div className={css.metadataText}>
        <span className={css.authorInfo}>
          <div>{authorsInfo}</div>
          <time>{formatDate(date)}</time>
        </span>
      </div>
    </div>
  );
}

type ImgProps = {
  author: string;
  className: string;
};

function AuthorImage({ author, className }: ImgProps) {
  const authorImgSrc = getAuthorImgSrc(author);
  return authorImgSrc ? (
    <img className={className} alt={author} src={authorImgSrc} />
  ) : (
    <span className={cx(className, css.authorLetter)}>
      {author.slice(0, 1)}
    </span>
  );
}
